import { z } from 'zod';
import { ContentEngineId, ContentNetwork, ContentPublicationFormat } from './content.js';
import { OffsetPageQuery, offsetPage } from './paging.js';
/**
 * Контракты раздела «Соцсети» (`/api/social/*`) — шлюз admin-api к модулю
 * комментариев контент-движка `wit_video_combine` (`/api/v2/social/*`).
 * Единственный источник истины, общий для роутов admin-api и модуля
 * `admin-module-social`. Wire-схемы самого движка сюда НЕ попадают: они живут
 * в одном файле адаптера `packages/social-engine`, здесь — то, что видит
 * админка.
 *
 * Данные (подключения, комментарии, очередь действий) живут в движке, у нас —
 * только просмотр, команды и аудит. Токен сети уходит в движок ОДИН раз
 * (`SocialConnectionPutRequest`) и наружу не возвращается никогда: в ответах
 * есть только `hasToken`, срок и владелец токена.
 *
 * Перечисления, по которым человек ДЕЙСТВУЕТ (статус подключения, режим,
 * статус действия, «что умеем»), — строгие: незнакомое значение должно
 * ронять разбор, а не молча показывать «активно». Поля только для показа
 * (`skipReason`, `error.code`, `lastError`) — строки: новая причина движка не
 * должна ломать экран, фразу для неё подберёт `describeSocialOutcome`
 * с запасной формулировкой.
 *
 * Тела запросов — `.strict()`: опечатка в имени поля на записи должна давать
 * 400, а не молча теряться.
 */
const Iso = z.string().datetime();
const Count = z.number().int().nonnegative();
const Name = z.string().min(1).max(200);
/** Соцсети движка — тот же список, что у «Контента» (один движок). */
export const SocialNetwork = ContentNetwork;
/**
 * Способ входа, которым выдан токен. Facebook Login (P3-E) — основной: через
 * него движок читает комментарии, отвечает, скрывает, удаляет и ставит
 * сердечко. Instagram Login — запасной: работает, только пока активного
 * Facebook Login нет, и сердечко не ставит. У аккаунта × сети могут жить оба
 * подключения сразу.
 */
export const SocialAuthType = z.enum(['instagram_login', 'facebook_login']);
/**
 * Какие способы входа бывают у сети (FB-SPEC §6.5). У Facebook — только
 * Facebook Login: Страница подключается токеном системного пользователя, а
 * Instagram Login к Facebook отношения не имеет (движок ответит 422
 * `unsupported_auth_type`). У остальных сетей — как было: оба способа (у сетей
 * без адаптера строка всё равно серая, «Скоро»). Порядок — как на экране:
 * Facebook Login первым (основной, P3-E).
 */
export const SOCIAL_AUTH_TYPES_BY_NETWORK = {
    instagram: ['facebook_login', 'instagram_login'],
    facebook: ['facebook_login'],
    tiktok: ['facebook_login', 'instagram_login'],
    youtube: ['facebook_login', 'instagram_login'],
    threads: ['facebook_login', 'instagram_login'],
};
/**
 * Имя сети для текстов UI («Facebook ограничил…»), если движок не прислал
 * своё `networkLabel` (старый движок). Совпадает со словарём имён сетей
 * движка (`NETWORK_LABEL`); подставляет admin-api.
 */
export const SOCIAL_NETWORK_LABEL = {
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    threads: 'Threads',
};
/**
 * Задачи системного пользователя на Странице Facebook (`/me/accounts.tasks`,
 * FB-SPEC §3.2) — справочник для подписей. В Business Settings они называются
 * переключателями: `CREATE_CONTENT` — «Контент» (сердечки), `MODERATE` —
 * «Активность сообщества» (ответы, скрытие, удаление), `MANAGE` — «Полный
 * доступ», `MESSAGING` — «Сообщения», `ADVERTISE` — «Реклама», `ANALYZE` —
 * «Статистика». В DTO поле — строки: новая задача сети не должна ронять экран.
 */
export const SOCIAL_PAGE_TASKS = [
    'MANAGE',
    'CREATE_CONTENT',
    'MODERATE',
    'MESSAGING',
    'ADVERTISE',
    'ANALYZE',
];
/**
 * Известные причины паузы (`safety.pauseReason`, `cleared[].reason`) —
 * справочник для фраз UI; в DTO поле — строка. `blocked_sibling` — действия
 * этой пары на паузе 48 ч, потому что другая сеть того же аккаунта получила
 * ограничение (368); `spam_sibling` — ответы на паузе 12 ч из-за спам-отказов
 * на другой сети аккаунта (FB-SPEC §4.2, M3). Снимает их «Возобновить» пары-
 * источника.
 */
export const SOCIAL_PAUSE_REASONS = [
    'blocked_368',
    'blocked_repeat',
    'rate_limited',
    'consecutive_failures',
    'spam_replies',
    'graph_load_high',
    'graph_load_poll',
    'manual_stop',
    'blocked_sibling',
    'spam_sibling',
];
/**
 * Известные коды ошибки подключения (`connection.lastError`) — справочник
 * для фраз UI; в DTO поле — строка. Facebook (FB-SPEC §2.4, §3.2):
 * `no_page_token` — сеть не отдала токен Страницы (действия невозможны),
 * `missing_scope_actions` — у токена нет `pages_manage_engagement` (только
 * чтение), `account_mismatch` — токен не видит Страницу/аккаунт.
 */
export const SOCIAL_CONNECTION_ERRORS = [
    'account_mismatch',
    'no_instagram',
    'missing_scope',
    'no_page_token',
    'missing_scope_actions',
];
/**
 * Роль подключения в паре аккаунт × сеть (P3-E, считает движок): `primary` —
 * через него идут опрос и действия; `fallback` — активно, но не основное
 * (запасное). null у DTO — подключение не активно или движок роль не прислал
 * (старый движок): экран бейдж не рисует.
 */
export const SocialConnectionRole = z.enum(['primary', 'fallback']);
/**
 * Состояние подключения: работает / истекло / сеть отвергла токен /
 * отключено в админке (токен удалён).
 */
export const SocialConnectionStatus = z.enum(['active', 'expired', 'invalid', 'disabled']);
/** Откуда токен: вставлен в админке / засеян из env движка / OAuth-кнопка (этап 2). */
export const SocialConnectionSource = z.enum(['admin', 'env', 'oauth']);
/**
 * Режим реагирования на аккаунт × сеть: «выключено — только журнал»,
 * «вручную — алгоритм предлагает, человек подтверждает», «тень — алгоритм
 * решает как в «Авто», но ничего не делает: решения только записываются для
 * просмотра» (RULINGS-SHADOW), «авто — исполняется без подтверждения».
 * Включить «Тень», как и «Авто», может только владелец.
 */
export const SocialReactionMode = z.enum(['off', 'manual', 'shadow', 'auto']);
/** Действие над комментарием: сердечко / ответ / скрыть / удалить. */
export const SocialActionKind = z.enum(['like', 'reply', 'hide', 'delete']);
/**
 * Статус действия в очереди-журнале. `proposed` — предложено алгоритмом и
 * ждёт человека; `approved` — подтверждено (или ручное), ждёт исполнителя;
 * `executing` — исполняется; `done` — выполнено (в сухом режиме — с
 * `dryRun: true`); `failed` / `skipped` / `rejected` — конечные.
 */
export const SocialActionStatus = z.enum([
    'proposed',
    'approved',
    'executing',
    'done',
    'failed',
    'skipped',
    'rejected',
]);
/** Кто создал действие: алгоритм-политика или человек из админки. */
export const SocialActionSource = z.enum(['policy', 'manual']);
/**
 * Состояние комментария: `new` — ждёт решения (алгоритм ещё не смотрел или в
 * режиме «вручную» ничего не предложил — тогда решает человек); `decided` —
 * есть хотя бы одно действие; `ignored` — без действий: алгоритм ничего не
 * предложил (`auto`/`off`), комментарий наш, или человек нажал «Отметить
 * просмотренным» (тогда заполнены `reviewedBy`/`reviewedAt`).
 */
export const SocialCommentState = z.enum(['new', 'decided', 'ignored']);
/**
 * Известные причины пропуска действия (`skipReason`). Только справочник для
 * фраз UI: в DTO поле — строка (см. шапку файла).
 */
export const SOCIAL_SKIP_REASONS = [
    'unsupported',
    'connection_missing',
    'comment_gone',
    'auto_delete_off',
    'comment_hidden',
    'not_allowed_by_network',
    'private_account',
    'own_comment',
    // Защита от блокировки (SAFE-E): анти-спам правила ответов и сердечек
    'comment_has_link',
    'reply_emoji_only',
    'reply_too_short',
    'reply_has_link',
    'reply_has_mention',
    'reply_author_post',
    'reply_author_day',
    'reply_text_repeated',
    'reply_text_burned',
    // FX-SE M2: авто-действие по комментарию старше 48 ч при исполнении
    'comment_stale',
    // engage-v1 (E-POL): заготовки ответа кончились; «только с человеком», одобрено автоматикой; шумный автор
    'no_template',
    'needs_approval',
    'noise_author',
];
/**
 * Категории комментария, которые ставит алгоритм `engage-v1` (AN-1 §1, §4).
 * В DTO поле — строка (только показ: новая категория движка не должна ронять
 * экран); перечисление — для фильтра «Категория» и для фраз UI.
 */
export const SOCIAL_COMMENT_CATEGORIES = [
    'praise',
    'request',
    'question',
    'tag_text',
    'tag_emoji',
    'tag_only',
    'relatable',
    'laugh',
    'emoji_love',
    'emoji_laugh',
    'emoji_other',
    'emoji_negative',
    'sad',
    'plot_talk',
    'personal_message',
    'fragment',
    'other',
    'empty',
    'negative',
    'toxic',
    'nsfw',
    'spam',
];
export const SocialCommentCategory = z.enum(SOCIAL_COMMENT_CATEGORIES);
/**
 * Язык комментария по алгоритму (AN-1 §3) — справочник для подписи. `none` —
 * нет слов (эмодзи, отметка), `tie` — португальский или испанский, не
 * отличить, `unk` — не определён. Прочие языки (ru, ko…) приходят строкой.
 * Фильтра по языку нет: движок (E-POL) по нему не фильтрует.
 */
export const SOCIAL_COMMENT_LANGS = ['pt', 'es', 'en', 'tie', 'none', 'unk'];
/**
 * Язык ответов по умолчанию (`defaultLang` настроек, AN-1 §6.9): на него
 * алгоритм отвечает на комментарий без слов (сердечки, отметка), если язык
 * поста и аккаунта определить не удалось. Умолчание движка — `en`.
 */
export const SocialReplyLang = z.enum(['en', 'pt', 'es']);
/**
 * Предложение алгоритма человеку по комментарию (ручная очередь AN-1 §5,
 * `comment.suggestion` движка): что сделать (`kind`: `reply` — ответить
 * вручную, `hide` — скрыть, `review` — посмотреть; строкой), черновик ответа,
 * почему (`reason`: `question`, `mentions_us`, `negative`, `toxic`, `nsfw`,
 * `spam`), фраза движка к причине (`reasonText`) и `pending` — ещё никто не
 * смотрел (нет отметки «просмотрен»). Только показ: чужая форма — null у
 * всего предложения.
 */
export const SocialCommentSuggestionDTO = z.object({
    kind: z.string().min(1),
    text: z.string().nullable().catch(null).default(null),
    reason: z.string().nullable().catch(null).default(null),
    reasonText: z.string().nullable().catch(null).default(null),
    /** true — в ручной очереди; false — уже посмотрели; null — движок не прислал. */
    pending: z.boolean().nullable().catch(null).default(null),
});
/**
 * Что решил бы алгоритм в режиме «Тень» (`shadow.kind` движка): четыре вида
 * действий, `manual` — отдал бы человеку (ручная очередь), `none` — ничего.
 * Только справочник для подписей: в DTO поле — строка (новое значение
 * движка не должно ронять экран, экран покажет код как есть).
 */
export const SOCIAL_SHADOW_KINDS = ['like', 'reply', 'hide', 'delete', 'manual', 'none'];
/**
 * Последнее теневое решение алгоритма по комментарию (RULINGS-SHADOW,
 * `comment.shadow` движка): что сделал бы (`kind`), текст ответа (выбранный
 * шаблон), почему (`reason` — код, `reasonText` — фраза движка), категория и
 * язык на момент решения, «упёрлось бы» (`wouldWaitReason` — лимит, окно
 * ответов, прогрев… по текущему состоянию; `wouldWaitText` — фраза движка),
 * когда решил. В сеть по нему НИЧЕГО не уходило и не уйдёт. Только показ:
 * чужая форма — null у всего решения.
 */
/**
 * Одно действие теневого решения (SHADOW-E `shadow.actions[]`): алгоритм
 * может решить и сердечко, и ответ сразу. `requiresApproval` — отдал бы
 * человеку; `skipReason` — пропустил бы (нет шаблона, ссылка в
 * комментарии…); `wouldWaitReason` — упёрлось бы. Только показ.
 */
export const SocialShadowActionDTO = z.object({
    kind: SocialActionKind,
    text: z.string().nullable().catch(null).default(null),
    reason: z.string().nullable().catch(null).default(null),
    requiresApproval: z.boolean().catch(false).default(false),
    skipReason: z.string().nullable().catch(null).default(null),
    skipText: z.string().nullable().catch(null).default(null),
    wouldWaitReason: z.string().nullable().catch(null).default(null),
    wouldWaitText: z.string().nullable().catch(null).default(null),
});
export const SocialShadowDecisionDTO = z.object({
    kind: z.string().min(1),
    text: z.string().nullable().catch(null).default(null),
    reason: z.string().nullable().catch(null).default(null),
    reasonText: z.string().nullable().catch(null).default(null),
    category: z.string().nullable().catch(null).default(null),
    lang: z.string().nullable().catch(null).default(null),
    wouldWaitReason: z.string().nullable().catch(null).default(null),
    wouldWaitText: z.string().nullable().catch(null).default(null),
    decidedAt: Iso.nullable().catch(null).default(null),
    /** Все действия решения (главное — в `kind`); чужие элементы отброшены адаптером. */
    actions: z.array(SocialShadowActionDTO).catch([]).default([]),
});
/** Идентификатор сущности движка (комментарий, действие) в пути. */
export const SocialEngineId = ContentEngineId;
// ---------------------------------------------------------------------------
// Общие куски
// ---------------------------------------------------------------------------
/**
 * Что можно сделать с комментариями при текущих подключениях аккаунта × сети
 * (объединение по активным подключениям). `why` — почему действие недоступно,
 * простыми словами, для подсказки у серой кнопки.
 */
export const SocialCapabilitiesDTO = z.object({
    listComments: z.boolean(),
    reply: z.boolean(),
    like: z.boolean(),
    hide: z.boolean(),
    delete: z.boolean(),
    why: z.partialRecord(SocialActionKind, z.string()).default({}),
});
/** Лимиты исполнителя на аккаунт × сеть. */
export const SocialLimitsDTO = z.object({
    /** Действий за один прогон исполнителя. */
    perRun: z.number().int().positive(),
    /** Пауза между действиями, секунд. */
    minGapSec: z.number().int().positive(),
    /** Действий в сутки (UTC), без сухого режима. */
    perDay: z.number().int().positive(),
    /** Сердечек за 5 секунд. */
    likesPer5s: z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// Защита аккаунтов от блокировки (RULINGS-SAFETY): лимиты по видам действий,
// прогрев, паузы и блокировка, общий «Стоп всех действий»
// ---------------------------------------------------------------------------
/**
 * Вид лимита: четыре вида действий и `total` — все действия аккаунта × сети
 * вместе. Окна скользящие (последний час, последние 24 часа), не UTC-сутки.
 */
export const SocialLimitKind = z.enum(['reply', 'like', 'hide', 'delete', 'total']);
/**
 * Потолки движка — ЗАПАСНАЯ копия его констант (RULINGS-SAFETY «Движок» п.1)
 * только для проверки формы, когда движок не прислал свои (`caps` →
 * `kindLimitCeilings`). Источник истины — движок: он сам отвергает значение
 * выше потолка, а admin-api потолки НЕ проверяет (RULINGS-SAFETY-2: шлюз не
 * должен отвергать то, что движок принимает). `null` — суточного лимита нет.
 */
export const SOCIAL_KIND_LIMIT_CEILINGS = {
    reply: { perHour: 20, per24h: 100 },
    like: { perHour: 40, per24h: 250 },
    hide: { perHour: 60, per24h: null },
    delete: { perHour: 20, per24h: null },
    total: { perHour: 60, per24h: null },
};
/** Умолчания движка (RULINGS-SAFETY «Движок» п.1) — для подсказок, не для записи. */
export const SOCIAL_KIND_LIMIT_DEFAULTS = {
    reply: { perHour: 15, per24h: 60 },
    like: { perHour: 30, per24h: 150 },
    hide: { perHour: 40, per24h: null },
    delete: { perHour: 10, per24h: null },
    total: { perHour: 60, per24h: null },
};
export const SocialKindLimitDTO = z.object({
    perHour: z.number().int().positive(),
    per24h: z.number().int().positive().nullable().default(null),
});
/** Лимиты по видам; вида нет в записи — движок его не прислал. */
export const SocialKindLimitsDTO = z.partialRecord(SocialLimitKind, SocialKindLimitDTO);
/** Случайная пауза между действиями одного вида, секунд: от `minSec` до `maxSec`. */
export const SocialIntervalDTO = z.object({
    minSec: z.number().int().nonnegative(),
    maxSec: z.number().int().nonnegative(),
});
/**
 * Профиль защиты одной сети (FB-SPEC §4.2, `safetyByNetwork[network]`
 * движка): потолки и умолчания лимитов по видам, паузы между действиями и
 * лимиты на один пост в час. У Facebook они ниже, чем у Instagram. Только
 * показ и проверка формы: источник истины — движок (он сам отвергает значение
 * выше потолка). Каждое поле терпимое: чужая форма — null / `{}`.
 */
export const SocialNetworkSafetyDTO = z.object({
    caps: SocialKindLimitsDTO.nullable().catch(null).default(null),
    defaults: SocialKindLimitsDTO.nullable().catch(null).default(null),
    intervalsSec: z.partialRecord(SocialActionKind, SocialIntervalDTO).catch({}).default({}),
    postLimitsPerHour: z
        .partialRecord(SocialActionKind, z.number().int().positive())
        .catch({})
        .default({}),
});
/**
 * Профили защиты по сетям (`safetyByNetwork` движка). null у ответа — движок
 * не прислал (старый движок): тогда потолки — верхнего уровня (= Instagram)
 * или {@link SOCIAL_KIND_LIMIT_CEILINGS}.
 */
export const SocialSafetyByNetworkDTO = z.partialRecord(SocialNetwork, SocialNetworkSafetyDTO);
/**
 * Сколько уже использовано из лимита вида — для «ответов 3 из 15 в час».
 * `perHour`/`per24h` — ДЕЙСТВУЮЩИЙ лимит (с учётом прогрева), null — нет.
 */
export const SocialKindUsageDTO = z.object({
    perHour: z.number().int().nonnegative().nullable().default(null),
    per24h: z.number().int().nonnegative().nullable().default(null),
    usedHour: Count,
    used24h: Count,
});
/**
 * Прогрев нового подключения: `day` — сколько дней прошло с первого
 * настоящего (не сухого) действия, `factor` — множитель лимитов
 * (дни 0–6 — 0,33; 7–13 — 0,66; дальше 1).
 */
export const SocialWarmupDTO = z.object({
    day: Count,
    factor: z.number().positive().max(1),
});
/**
 * Общий суточный лимит всех действий аккаунта × сети (`safety.dailyTotal`
 * движка, FX-SE L5): `usedToday` боевых действий из `perDay` за сутки UTC,
 * `resetsAt` — когда счётчик обнулится (00:00 UTC), `text` — пояснение движка.
 * «за сутки (общий): 40 из 150».
 */
export const SocialDailyTotalDTO = z.object({
    perDay: z.number().int().positive(),
    usedToday: Count,
    resetsAt: Iso.nullable().default(null),
    text: z.string().nullable().default(null),
});
/**
 * Состояние защиты аккаунта × сети (`safety` ячейки, контракт SAFE-E).
 * `pausedUntil` — все действия на паузе до (сеть попросила подождать,
 * блокировка 368, серия отказов…); `pauseReason` — код, `pauseText` — фраза
 * движка. `replyPausedUntil` — на паузе только ответы (отказы антиспама),
 * `pollPausedUntil` — только сбор комментариев (нагрузка API). `blockedAt` —
 * повторная блокировка: режим переведён в «Выключено», действия не пойдут,
 * пока владелец не нажмёт «Возобновить». `stoppedAt`/`stoppedBy` — действия
 * аккаунта остановлены вручную. Фразы (`*Text`) — готовые русские тексты
 * движка; нет — экран подбирает свою по коду. `status` — сводное состояние
 * (`active` | `blocked` | `global_stop` | `manual_stop` | `mode_off` |
 * `paused`) строкой, а не enum: новое состояние движка не должно ронять экран.
 */
export const SocialSafetyDTO = z.object({
    status: z.string().nullable().default(null),
    statusText: z.string().nullable().default(null),
    pausedUntil: Iso.nullable().default(null),
    pauseReason: z.string().nullable().default(null),
    pauseText: z.string().nullable().default(null),
    replyPausedUntil: Iso.nullable().default(null),
    replyPauseText: z.string().nullable().default(null),
    /** Код причины паузы ответов (`spam_replies` | `spam_sibling`). */
    replyPauseReason: z.string().nullable().catch(null).default(null),
    /**
     * Сеть-источник паузы `blocked_sibling` / `spam_sibling`; null — пауза своя
     * (или старый движок не прислал: экран тогда вычисляет сеть сам).
     */
    siblingNetwork: SocialNetwork.nullable().catch(null).default(null),
    pollPausedUntil: Iso.nullable().default(null),
    pollPauseText: z.string().nullable().default(null),
    blockedAt: Iso.nullable().default(null),
    stoppedAt: Iso.nullable().default(null),
    stoppedBy: z.string().nullable().default(null),
    warmup: SocialWarmupDTO.nullable().default(null),
    limits: z.partialRecord(SocialLimitKind, SocialKindUsageDTO).default({}),
    /**
     * Общий суточный лимит всех действий (легаси `perDay`, сутки UTC) — действует
     * поверх лимитов по виду (FX-SE L5). null — движок не отдаёт.
     */
    dailyTotal: SocialDailyTotalDTO.nullable().default(null),
});
/**
 * Общий «Стоп всех действий» (настройка движка `social_global`): ни одно
 * действие ни одного аккаунта не исполняется, пока владелец не возобновит.
 * Комментарии при этом собираются. `by`/`at` — кто и когда остановил
 * (`stoppedBy`/`stoppedAt` движка; повторный стоп их не перезаписывает).
 */
export const SocialGlobalStopDTO = z.object({
    stopped: z.boolean(),
    by: z.string().nullable().default(null),
    at: Iso.nullable().default(null),
    /** Причина, которую указали при стопе; null — не указали. */
    reason: z.string().nullable().default(null),
});
// ---------------------------------------------------------------------------
// GET /connections, PUT/POST/DELETE /connections/:account/:network/:authType
// ---------------------------------------------------------------------------
/**
 * Одно подключение (токен) аккаунта × сети × способа входа. Самого токена
 * здесь нет и быть не может — только факт `hasToken`, срок и владелец.
 */
export const SocialConnectionDTO = z.object({
    authType: SocialAuthType,
    /**
     * Основное / запасное (P3-E) — только показ бейджа; чужое значение и
     * отсутствие (старый движок) — null.
     */
    role: SocialConnectionRole.nullable().catch(null).default(null),
    status: SocialConnectionStatus,
    hasToken: z.boolean(),
    source: SocialConnectionSource,
    /** id профессионального аккаунта в сети (у Instagram Login — `user_id` из `/me`). */
    externalUserId: z.string().nullable().default(null),
    externalUsername: z.string().nullable().default(null),
    /** Страница Facebook (только Facebook Login). */
    pageId: z.string().nullable().default(null),
    /** До какого момента действует токен; null — неизвестно. */
    tokenExpiresAt: Iso.nullable().default(null),
    /**
     * true — срок ПРЕДПОЛАГАЕМЫЙ (вставленный токен: подключение + 60 дней),
     * пока продление не вернёт точный `expires_in`. UI пишет «(предположительно)».
     */
    expiresAssumed: z.boolean(),
    /** Права токена; у вставленного вручную их не знаем — пустой список не ошибка. */
    scopes: z.array(z.string()).default([]),
    /** Кто подключил (`X-Actor` движка). */
    connectedBy: z.string(),
    connectedAt: Iso,
    lastCheckedAt: Iso.nullable().default(null),
    lastRefreshAt: Iso.nullable().default(null),
    /** Код/подкод ошибки сети (не текст: там бывает username); null — ошибок нет. */
    lastError: z.string().nullable().default(null),
    /** Опрос этим подключением на паузе до (лимиты/блок сети); null — не на паузе. */
    pausedUntil: Iso.nullable().default(null),
    /**
     * Facebook (FB-SPEC §3.2): движок получил и хранит токен Страницы (им идут
     * все действия). Самого токена здесь нет — только факт. Старый движок / не
     * Facebook — false.
     */
    hasPageToken: z.boolean().catch(false).default(false),
    /**
     * Задачи системного пользователя на Странице (`MODERATE`, `CREATE_CONTENT`,
     * `MANAGE`…, см. {@link SOCIAL_PAGE_TASKS}) строками. null — неизвестно
     * (старый движок, не Facebook или сеть задачи не отдала — тогда умения не
     * режутся, `capabilities` скажет сам).
     */
    pageTasks: z.array(z.string()).nullable().catch(null).default(null),
    /** Имя Страницы Facebook («Jen and Jeffy») для подписи; null — нет / старый движок. */
    externalName: z.string().nullable().catch(null).default(null),
});
/**
 * Настройки реагирования на аккаунт × сеть. `updatedAt`/`author` — null, пока
 * настройки ни разу не сохраняли (действуют умолчания движка).
 */
export const SocialSettingsDTO = z.object({
    account: Name,
    network: SocialNetwork,
    mode: SocialReactionMode,
    /**
     * Имя алгоритма-политики: `engage-v1` — «Алгоритм v1» (AN-1), `noop` —
     * «Нет — только вручную» (ничего не предлагает).
     */
    policy: z.string().min(1),
    /** Исполнять удаление, предложенное алгоритмом, без человека (даже в `auto`). */
    autoDelete: z.boolean(),
    /** Сколько последних постов аккаунта проверять. */
    pollMediaCount: z.number().int().positive(),
    limits: SocialLimitsDTO,
    /**
     * Лимиты по видам действий (в час / за 24 часа). null — движок их ещё не
     * отдаёт (выкат: новый экран против старого движка).
     */
    kindLimits: SocialKindLimitsDTO.nullable().default(null),
    /**
     * Потолки движка ПО СЕТИ ПАРЫ (`safetyByNetwork[network].caps`, у старого
     * движка — `caps` верхнего уровня `GET /settings`); null — не прислал
     * (например, в ответе на правку), экран берёт `SOCIAL_KIND_LIMIT_CEILINGS`.
     */
    kindLimitCeilings: SocialKindLimitsDTO.nullable().default(null),
    /** Язык ответов по умолчанию (engage-v1); null — движок не отдаёт (старый). */
    defaultLang: SocialReplyLang.nullable().catch(null).default(null),
    updatedAt: Iso.nullable().default(null),
    author: z.string().nullable().default(null),
});
/**
 * Строка таблицы «Аккаунт × Сеть». `supported: false` — для сети нет адаптера
 * (строка серая, «Скоро»). `lastPoll*` — с основного подключения
 * (`primaryAuthType`: Facebook Login, если активно, иначе Instagram Login).
 */
export const SocialConnectionCellDTO = z.object({
    account: Name,
    network: SocialNetwork,
    /**
     * Имя сети для текстов UI («Instagram» / «Facebook»): движок или, если он
     * не прислал, admin-api по {@link SOCIAL_NETWORK_LABEL}. null — только у
     * старого admin-api (выкат: новый экран против старого шлюза).
     */
    networkLabel: z.string().min(1).nullable().catch(null).default(null),
    supported: z.boolean(),
    connections: z.array(SocialConnectionDTO),
    /**
     * Через какое подключение идут опрос и действия (P3-E); null — активных
     * подключений нет или движок не прислал (старый движок). Только показ.
     */
    primaryAuthType: SocialAuthType.nullable().catch(null).default(null),
    /**
     * Кто возьмёт работу, если основное перестанет действовать (RV-P3 M1);
     * null — запасного пригодного подключения нет (или движок не прислал).
     * По нему экран решает, писать ли «работа перейдёт на запасное».
     */
    fallbackAuthType: SocialAuthType.nullable().catch(null).default(null),
    capabilities: SocialCapabilitiesDTO,
    /** null — только у `supported:false`: для сети без адаптера настраивать нечего. */
    settings: SocialSettingsDTO.nullable().default(null),
    lastPollAt: Iso.nullable().default(null),
    lastPollError: z.string().nullable().default(null),
    /**
     * Пауза, блокировка, прогрев и использованные лимиты аккаунта × сети.
     * null — движок их ещё не отдаёт (или сеть без адаптера).
     */
    safety: SocialSafetyDTO.nullable().default(null),
});
/**
 * `enabled: false` — модуль комментариев в движке выключен (`ENGINE_SOCIAL`):
 * экран показывает «не настроено» без пробных 409. `dryRun` — баннер сухого
 * режима. `secretsConfigured: false` — в движке нет ключа шифрования, кнопка
 * «Подключить токен» недоступна.
 */
export const SocialConnectionsResponse = z.object({
    enabled: z.boolean(),
    dryRun: z.boolean(),
    secretsConfigured: z.boolean(),
    items: z.array(SocialConnectionCellDTO),
    /** Общий «Стоп всех действий»; null — движок его ещё не отдаёт. */
    globalStop: SocialGlobalStopDTO.nullable().default(null),
    /**
     * Откуда `dryRun` (FX-SE3 L3): `engine` — по последнему тику исполнителя
     * engine (`dryRunReportedAt`), `admin_env` — engine ещё не тикал. null —
     * движок не прислал. Только показ («по данным движка на HH:MM»).
     */
    dryRunSource: z.enum(['engine', 'admin_env']).nullable().catch(null).default(null),
    dryRunReportedAt: Iso.nullable().catch(null).default(null),
    /** Профили защиты по сетям (FB-SPEC §4.2); null — движок не прислал (старый). */
    safetyByNetwork: SocialSafetyByNetworkDTO.nullable().catch(null).default(null),
});
/**
 * Ответ на `POST /stop` и `POST /resume` — состояние общего стопа после
 * операции. Тела у запросов нет.
 */
export const SocialGlobalStopResponse = SocialGlobalStopDTO;
/** Путь аккаунта × сети: `/connections/:account/:network/stop|resume`. */
export const SocialConnectionPairParams = z.object({
    account: Name,
    network: SocialNetwork,
});
/** Путь возобновления аккаунта: `/connections/:account/:network/resume`. */
export const SocialConnectionResumeParams = SocialConnectionPairParams;
/**
 * Тело «Возобновить» аккаунт (RULINGS-SAFETY-3 M2). Пока у пары действует
 * пауза после ограничения Instagram (368: `blocked_368`, `blocked_repeat`),
 * движок снимает её только с `confirmBlock: true` — человек подтвердил, что в
 * приложении Instagram → «Статус аккаунта» ограничение снято; без него — 409
 * `social_block_confirm_required`. Ручной стоп без паузы 368 — без тела (как
 * раньше). Тело необязательно: запрос без тела = `{}`.
 */
export const SocialConnectionResumeRequest = z
    .object({
    confirmBlock: z.boolean().optional(),
})
    .strict();
/** Путь ручного стопа аккаунта: `/connections/:account/:network/stop`. */
export const SocialConnectionStopParams = SocialConnectionPairParams;
/**
 * Одна пауза в ответе «Возобновить»: код причины (`blocked_368`,
 * `manual_stop`, `rate_limited`, `global_stop`, `mode_off`…), фраза движка
 * (null — экран подберёт свою по коду) и до какого момента (null — без срока,
 * например ручной стоп). У снятой блокировки (`blocked`) движок кладёт в
 * `until` момент блокировки — экран срок у снятых не показывает.
 */
export const SocialPauseInfoDTO = z.object({
    reason: z.string().min(1),
    text: z.string().nullable().default(null),
    until: Iso.nullable().default(null),
    /**
     * Где действует (`pair` | `replies` | `poll` | `connection` | `global` |
     * `mode`) — строкой, только для показа; null — движок не прислал.
     */
    scope: z.string().nullable().default(null),
    /** У паузы одного подключения — его способ входа (`instagram_login`…); иначе null. */
    authType: z.string().nullable().default(null),
});
/**
 * Ответ на «Возобновить» (RULINGS-SAFETY-2 M3). Движок снимает только
 * блокировку (`blockedAt`, паузу 368) и ручной стоп; паузы троттлинга,
 * нагрузки API, серии отказов и паузу ответов за спам НЕ снимает — они
 * истекают сами. `cleared` — что снято, `remaining` — что осталось и до
 * когда. null — движок перечень не прислал (старый движок): экран говорит
 * просто «возобновлено».
 */
export const SocialConnectionResumeResponse = z.object({
    resumed: z.literal(true),
    cleared: z.array(SocialPauseInfoDTO).nullable().default(null),
    remaining: z.array(SocialPauseInfoDTO).nullable().default(null),
    /**
     * Итоговое состояние пары после «Возобновить» (`active` | `paused` |
     * `global_stop` | `mode_off` | …) и фраза движка; null — не прислал.
     */
    status: z.string().nullable().default(null),
    statusText: z.string().nullable().default(null),
    /**
     * Режим, в который движок вернул аккаунт при снятии повторной блокировки
     * (RULINGS-SAFETY-3 M1: всегда `manual`, даже если до блокировки было
     * «Авто» — «Авто» владелец включает отдельно). null — режим не менялся
     * (блокировки не было) или движок не прислал.
     */
    modeRestored: SocialReactionMode.nullable().catch(null).default(null),
    /**
     * Режим до повторной блокировки (`resume.modeBeforeBlock` движка FX-SE3) —
     * чтобы сказать «до блокировки был «Авто»». null — блокировки не было или
     * движок не прислал.
     */
    modeBeforeBlock: SocialReactionMode.nullable().catch(null).default(null),
});
/**
 * Ответ на «Остановить аккаунт»: действия аккаунта × сети не уходят в сеть,
 * пока владелец не нажмёт «Возобновить». Комментарии собираются. Повтор
 * идемпотентен (кто и когда остановил — первый стоп).
 */
export const SocialConnectionStopResponse = z.object({ stopped: z.literal(true) });
/** Путь подключения: `/connections/:account/:network/:authType`. */
export const SocialConnectionParams = z.object({
    account: Name,
    network: SocialNetwork,
    authType: SocialAuthType,
});
/**
 * Подключить (или заменить) токен. Поле объявлено секретом: `ZodForm` кита
 * рендерит `type=password`, не держит значение и шлёт его только при
 * изменении. admin-api тело НЕ логирует и в аудит пишет только факт
 * `tokenRotated: true`. Пробелы по краям (хвост при копировании) срезаются.
 */
export const SocialConnectionPutRequest = z
    .object({
    token: z.string().trim().min(1).max(4096).meta({ secret: true, label: 'Токен' }),
})
    .strict();
/**
 * Сети, из подключения которых можно взять токен для «Подключить тем же
 * токеном» (FB-SPEC §0.4): Страница и Instagram-аккаунт одного бизнеса
 * работают одним токеном системного пользователя.
 */
export const SocialReuseSourceNetwork = z.enum(['instagram', 'facebook']);
/**
 * «Подключить тем же токеном, что у Instagram» (`POST
 * /connections/:account/:network/:authType/reuse`): движок сам копирует
 * зашифрованный токен подключения `(account, fromNetwork, authType)` и
 * проверяет его, как при вставке. Токена в запросе нет и в ответе нет.
 * Сеть-источник должна отличаться от сети пути (проверяет admin-api).
 */
export const SocialConnectionReuseRequest = z
    .object({
    fromNetwork: SocialReuseSourceNetwork,
})
    .strict();
/** Ответ на подключение / проверку — подключение после операции. */
export const SocialConnectionResponse = SocialConnectionDTO;
/** Ответ на отключение: токен удалён, подключение `disabled`. */
export const SocialConnectionDeleteResponse = z.object({ deleted: z.literal(true) });
// ---------------------------------------------------------------------------
// GET /settings, PUT /settings/:account/:network, GET /policies
// ---------------------------------------------------------------------------
export const SocialSettingsResponse = z.object({
    enabled: z.boolean(),
    dryRun: z.boolean(),
    items: z.array(SocialSettingsDTO),
    /**
     * Профили защиты по сетям (FB-SPEC §4.2); null — движок не прислал. Потолки
     * пары уже разложены в `items[].kindLimitCeilings` по её сети.
     */
    safetyByNetwork: SocialSafetyByNetworkDTO.nullable().catch(null).default(null),
});
/** Путь настроек: `/settings/:account/:network`. */
export const SocialSettingsParams = z.object({
    account: Name,
    network: SocialNetwork,
});
/** Правка лимитов — любые из четырёх; границы те же, что у движка. */
export const SocialLimitsUpdate = z
    .object({
    perRun: z.number().int().min(1).max(50).optional(),
    minGapSec: z.number().int().min(1).max(60).optional(),
    perDay: z.number().int().min(1).max(1000).optional(),
    likesPer5s: z.number().int().min(1).max(10).optional(),
})
    .strict();
/** Окна одного вида в правке: любое из двух; пустая правка — 400 (общий refine запроса). */
const KindLimitWindowsUpdate = z
    .object({
    perHour: z.number().int().min(1).optional(),
    per24h: z.number().int().min(1).optional(),
})
    .strict();
/**
 * Правка лимитов по видам: любые виды, любое из двух окон. Потолки здесь НЕ
 * проверяются (RULINGS-SAFETY-2): их знает и проверяет движок (`422/400
 * validation_failed` с понятной фразой), а копия в шлюзе отвергала бы то, что
 * движок уже разрешил после смены своих констант. Форма проверяет потолок по
 * `caps` движка (запасной вариант — {@link SOCIAL_KIND_LIMIT_CEILINGS}).
 */
export const SocialKindLimitsUpdate = z
    .object({
    reply: KindLimitWindowsUpdate.optional(),
    like: KindLimitWindowsUpdate.optional(),
    hide: KindLimitWindowsUpdate.optional(),
    delete: KindLimitWindowsUpdate.optional(),
    total: KindLimitWindowsUpdate.optional(),
})
    .strict();
/**
 * Правка настроек: хотя бы одно поле. Неизвестное имя политики проверяет
 * движок (`validation_failed`) — список политик живёт у него.
 *
 * Включающие изменения — только владелец (`social.connections_manage`,
 * RULINGS-SAFETY-2): перевод режима в «Авто» или «Тень» (RULINGS-SHADOW), включение автоудаления, любое
 * изменение старых `limits`, повышение `kindLimits`. Выключающие — супервайзер
 * тоже: выключить «Авто», выключить автоудаление, снизить лимит по виду.
 * Алгоритм (`policy`) — по правам как режим: выбрать настоящий алгоритм
 * (не `noop`) для аккаунта в «Авто» — только владелец; в «Вручную» и
 * «Выключено», а также «Нет — только вручную» (`noop`) — и супервайзер.
 * Проверяет admin-api по изменившимся полям.
 */
export const SocialSettingsUpdateRequest = z
    .object({
    mode: SocialReactionMode.optional(),
    policy: z.string().trim().min(1).max(100).optional(),
    autoDelete: z.boolean().optional(),
    pollMediaCount: z.number().int().min(1).max(50).optional(),
    limits: SocialLimitsUpdate.optional(),
    kindLimits: SocialKindLimitsUpdate.optional(),
    defaultLang: SocialReplyLang.optional(),
})
    .strict()
    .refine((b) => b.mode !== undefined ||
    b.defaultLang !== undefined ||
    b.policy !== undefined ||
    b.autoDelete !== undefined ||
    b.pollMediaCount !== undefined ||
    (b.limits !== undefined && Object.keys(b.limits).length > 0) ||
    (b.kindLimits !== undefined &&
        Object.values(b.kindLimits).some((v) => v !== undefined && Object.keys(v).length > 0)), { message: 'нужно указать хотя бы одну настройку' });
/**
 * Ответ на правку — настройки после изменения и `rejectedPolicyActions`:
 * сколько действий алгоритма движок снял из очереди при смене режима на
 * «Тень»/«Выключено» (RV-SHADOW M1; в журнале — `rejectReason:
 * 'mode_changed'`). null — движок не прислал (старый) или правки не было.
 */
export const SocialSettingsUpdateResponse = SocialSettingsDTO.extend({
    rejectedPolicyActions: Count.nullable().catch(null).default(null),
});
/** Алгоритм выбора действия; `description` — простыми словами для select. */
export const SocialPolicyDTO = z.object({
    name: z.string().min(1),
    description: z.string(),
});
export const SocialPoliciesResponse = z.object({
    enabled: z.boolean(),
    dryRun: z.boolean(),
    items: z.array(SocialPolicyDTO),
});
// ---------------------------------------------------------------------------
// Действия: GET /actions, POST /comments/:id/actions, POST /actions/:id/approve|reject
// ---------------------------------------------------------------------------
/** Ошибка сети у действия: машинные поля, без текста сети. */
export const SocialActionErrorDTO = z.object({
    code: z.string().min(1),
    graphCode: z.number().int().nullable().default(null),
    graphSubcode: z.number().int().nullable().default(null),
});
export const SocialActionDTO = z.object({
    id: z.string().min(1),
    /** id комментария в журнале движка (для перехода в карточку). */
    commentRef: z.string().min(1),
    kind: SocialActionKind,
    /** Текст ответа (только `reply`); null — у действия нет текста. */
    text: z.string().nullable().default(null),
    source: SocialActionSource,
    policy: z.string().nullable().default(null),
    /** Кто создал: `X-Actor` админки или `policy:<имя>`. */
    author: z.string(),
    status: SocialActionStatus,
    attempts: Count,
    nextAttemptAt: Iso.nullable().default(null),
    approvedBy: z.string().nullable().default(null),
    rejectedBy: z.string().nullable().default(null),
    rejectReason: z.string().nullable().default(null),
    finishedAt: Iso.nullable().default(null),
    /** true — выполнено в сухом режиме: в сеть не уходило. */
    dryRun: z.boolean(),
    error: SocialActionErrorDTO.nullable().default(null),
    /** Почему пропущено (строка движка, см. `SOCIAL_SKIP_REASONS`); null — не пропущено. */
    skipReason: z.string().nullable().default(null),
    /** Готовая русская фраза движка к `skipReason`; null — экран берёт свою. */
    skipText: z.string().nullable().default(null),
    /** Почему подтверждённое действие сейчас ждёт (код движка, только у `approved`). */
    waitReason: z.string().nullable().default(null),
    /** Фраза к `waitReason`: «Достигнут лимит этого вида действий за час»… */
    waitText: z.string().nullable().default(null),
    /** Фраза к `error.code`: «Instagram отклонил ответ как спам…». */
    errorText: z.string().nullable().default(null),
    /** id созданного в сети объекта (ответа); null — нет. */
    resultId: z.string().nullable().default(null),
    /**
     * Почему алгоритм предложил (`engage-v1`): категория комментария или
     * `spam_link`. null — ручное действие или старый движок. Только показ.
     */
    reason: z.string().nullable().catch(null).default(null),
    /**
     * true — только после подтверждения человеком: движок не исполняет его сам
     * ни в каком режиме, даже в «Авто». null — движок не прислал.
     */
    requiresApproval: z.boolean().nullable().catch(null).default(null),
    /**
     * Кто и когда исправил текст ответа при подтверждении (E-POL); null — не
     * исправляли (или старый движок). Только показ.
     */
    editedBy: z.string().nullable().catch(null).default(null),
    editedAt: Iso.nullable().catch(null).default(null),
    createdAt: Iso,
});
export const SocialActionListQuery = OffsetPageQuery.extend({
    account: Name.optional(),
    network: SocialNetwork.optional(),
    status: SocialActionStatus.optional(),
    kind: SocialActionKind.optional(),
    source: SocialActionSource.optional(),
});
export const SocialActionListResponse = offsetPage(SocialActionDTO);
/** Предел текста ответа — ограничение Instagram (как у подписи). */
export const SOCIAL_REPLY_MAX_LENGTH = 2200;
/**
 * Ручной запрос действия над комментарием. Это же — «Повторить» после
 * `failed`/`skipped`: создаётся новая запись, старая остаётся в журнале.
 * Текст обязателен у ответа и запрещён у остальных действий.
 */
export const SocialActionRequest = z
    .object({
    kind: SocialActionKind,
    text: z.string().trim().min(1).max(SOCIAL_REPLY_MAX_LENGTH).optional(),
})
    .strict()
    .refine((b) => b.kind !== 'reply' || b.text !== undefined, {
    message: 'для ответа нужен текст',
    path: ['text'],
})
    .refine((b) => b.kind === 'reply' || b.text === undefined, {
    message: 'текст бывает только у ответа',
    path: ['text'],
});
/** Отклонение предложенного действия; причина необязательна. */
export const SocialActionRejectRequest = z
    .object({
    reason: z.string().trim().max(200).optional(),
})
    .strict();
/**
 * Подтверждение предложенного действия. У ответа человек может исправить
 * текст перед «Подтвердить»: `text` уходит в движок вместе с подтверждением,
 * движок проверяет его правилами защиты (422 `social_action_unsafe` —
 * действие не меняется) и пишет `editedBy`/`editedAt`. Только у `reply`
 * (у другого вида движок ответит 400). Без тела — подтвердить как есть.
 */
export const SocialActionApproveRequest = z
    .object({
    text: z.string().trim().min(1).max(SOCIAL_REPLY_MAX_LENGTH).optional(),
})
    .strict();
/** Ответ на запрос/подтверждение/отклонение — действие после операции. */
export const SocialActionResponse = SocialActionDTO;
// ---------------------------------------------------------------------------
// Комментарии: GET /comments, GET /comments/:id
// ---------------------------------------------------------------------------
/** Отметка человека в комментарии (Facebook `message_tags`): только имя, для показа. */
export const SocialCommentTagDTO = z.object({
    name: z.string().min(1),
});
export const SocialCommentDTO = z.object({
    id: z.string().min(1),
    account: Name,
    network: SocialNetwork,
    /** id поста в сети. */
    networkPostId: z.string().min(1),
    /** Публикация движка, если пост связан; null — пост не из движка или не связан. */
    publicationId: z.string().nullable().default(null),
    /**
     * Внешняя ссылка на пост в сети; null — ссылки нет. Ссылка внешняя, а не на
     * карточку «Контента»: модуль «Соцсети» от «Контента» не зависит.
     */
    postUrl: z.string().url().nullable().default(null),
    /** id комментария в сети. */
    commentId: z.string().min(1),
    /** Родительский комментарий (у ответа); null — верхний уровень. */
    parentId: z.string().nullable().default(null),
    /** Username автора (Instagram); у Facebook — null, там только имя ({@link authorName}). */
    authorUsername: z.string().nullable().default(null),
    /**
     * Имя автора (Facebook `from.name`, FB-SPEC §5.2 п.6): не уникально и
     * только для показа. Экран: автор = `authorName ?? authorUsername`. null —
     * нет / Instagram / старый движок.
     */
    authorName: z.string().nullable().catch(null).default(null),
    /**
     * Отметки людей в тексте (Facebook `message_tags`) — только имена, для
     * бейджа «отметил: …». Старый движок / Instagram — `[]`.
     */
    tags: z.array(SocialCommentTagDTO).catch([]).default([]),
    /** Стикер / GIF / фото (вложение) — для бейджа; старый движок — false. */
    hasAttachment: z.boolean().catch(false).default(false),
    /** true — комментарий нашего аккаунта (алгоритм на него не реагирует). */
    isOwn: z.boolean(),
    text: z.string(),
    mentions: z.array(z.string()).default([]),
    hasLink: z.boolean(),
    hidden: z.boolean().nullable().default(null),
    likeCount: Count.nullable().default(null),
    replyCount: Count,
    /** Когда комментарий оставлен (время сети). */
    createdAt: Iso,
    /** Когда движок его забрал. */
    fetchedAt: Iso,
    state: SocialCommentState,
    likedByUs: z.boolean(),
    repliedByUs: z.boolean(),
    deleted: z.boolean(),
    /** Предложенные и ждущие человека действия (`proposed`). */
    pending: z.array(SocialActionDTO).default([]),
    /**
     * Виды выполненных действий — и настоящих, и отмеченных в сухом режиме
     * (`dryRun`: в сеть не уходило). Какие из них сухие — {@link doneDry}.
     */
    done: z.array(SocialActionKind).default([]),
    /**
     * Виды из `done`, выполненные ТОЛЬКО в сухом режиме (в сеть не уходили; у
     * вида есть и настоящее выполнение — его здесь нет). null — движок не
     * прислал (старый движок): экран выводит по `likedByUs`/`repliedByUs`/
     * `hidden`/`deleted` — их движок ставит только после настоящего вызова сети.
     */
    doneDry: z.array(SocialActionKind).nullable().catch(null).default(null),
    /** Кто отметил «Просмотрен — без действий» (`X-Actor`); null — не отмечали. */
    reviewedBy: z.string().nullable().default(null),
    /** Когда отметили; null — не отмечали. */
    reviewedAt: Iso.nullable().default(null),
    /**
     * Категория по алгоритму `engage-v1` (`praise`, `tag_only`, `question`,
     * `toxic`… — см. {@link SOCIAL_COMMENT_CATEGORIES}) строкой: только показ.
     * null — алгоритм комментарий не смотрел (политика «noop», старый движок).
     */
    category: z.string().nullable().catch(null).default(null),
    /** Язык по алгоритму (`pt`/`es`/`en`/`none`/`tie`/`unk`/`ru`…) строкой; null — не определяли. */
    lang: z.string().nullable().catch(null).default(null),
    /** Признаки текста (`url`, `mention`, `qmark`, `profanity`…) — только показ. */
    flags: z.array(z.string()).catch([]).default([]),
    /**
     * Предложение алгоритма человеку (ручная очередь). Бывает и без
     * предложенного действия: вопрос к каналу алгоритм не ставит в очередь, а
     * отдаёт человеку. null — нет / старый движок.
     */
    suggestion: SocialCommentSuggestionDTO.nullable().catch(null).default(null),
    /**
     * Последнее решение алгоритма в режиме «Тень» — что он сделал бы. null — в
     * тени комментарий не решали (или старый движок).
     */
    shadow: SocialShadowDecisionDTO.nullable().catch(null).default(null),
});
/**
 * Ответ на `POST /comments/:id/dismiss` («Отметить просмотренным») —
 * комментарий после отметки (`state: 'ignored'`, `reviewedBy`/`reviewedAt`).
 * Повтор идемпотентен: движок отвечает 200 тем же комментарием. Тела у
 * запроса нет.
 */
export const SocialCommentDismissResponse = SocialCommentDTO;
/**
 * Фильтры журнала комментариев (query-string). `pending=true` — только с
 * предложениями, ждущими человека. Строгий enum, а не `z.coerce.boolean()`:
 * `Boolean('false') === true`. `from < to` — сравнение моментов, не строк:
 * «…10:00:00Z» и «…10:00:00.500Z» лексикографически идут наоборот.
 */
export const SocialCommentListQuery = OffsetPageQuery.extend({
    account: Name.optional(),
    network: SocialNetwork.optional(),
    state: SocialCommentState.optional(),
    pending: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    /** Только ответы на комментарий с этим id в сети (ветка ответов). */
    parentId: z.string().trim().min(1).max(200).optional(),
    from: Iso.optional(),
    to: Iso.optional(),
    /** Подстрока текста комментария. */
    q: z.string().trim().min(1).max(100).optional(),
    /** Категория по алгоритму (фильтр «Категория»). */
    category: SocialCommentCategory.optional(),
    /**
     * `true` — ручная очередь «Нужен человек»: алгоритм отдал комментарий
     * человеку (`suggestion` вопрос к каналу, негатив, токсичное, 18+, спам без
     * явной ссылки, упоминание нас) и его ещё не отметили просмотренным
     * (движок: `suggestion=true`). Строгий enum, как `pending`.
     */
    needsHuman: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    /**
     * `true` — только комментарии, которые решал алгоритм в режиме «Тень»
     * (движок: `shadow=true`); `false` — которые тень не решала. Строгий enum.
     */
    shadow: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
}).refine((q) => !(q.from && q.to) || Date.parse(q.from) < Date.parse(q.to), {
    message: 'from должен быть раньше to',
    path: ['from'],
});
export const SocialCommentListResponse = offsetPage(SocialCommentDTO);
/** Публикация движка, под которой комментарий (коротко — для карточки). */
export const SocialCommentPublicationDTO = z.object({
    id: z.string().min(1),
    hookClipName: z.string().nullable().default(null),
    /** Внешняя ссылка на пост; null — движок ещё не связал публикацию с постом. */
    url: z.string().url().nullable().default(null),
    format: ContentPublicationFormat.nullable().default(null),
});
/** Карточка комментария: сам комментарий, весь журнал действий, пост, «что умеем». */
export const SocialCommentDetailResponse = z.object({
    comment: SocialCommentDTO,
    actions: z.array(SocialActionDTO),
    publication: SocialCommentPublicationDTO.nullable().default(null),
    capabilities: SocialCapabilitiesDTO,
});
// ---------------------------------------------------------------------------
// GET /shadow/summary — «Тень за период»
// ---------------------------------------------------------------------------
/**
 * Сводка решений алгоритма в режиме «Тень» за период (RULINGS-SHADOW): по
 * аккаунту × сети или по всем. `from < to` — сравнение моментов, как у
 * комментариев. Без периода — решает движок (по умолчанию — последние 24 ч).
 */
export const SocialShadowSummaryQuery = z
    .object({
    account: Name.optional(),
    network: SocialNetwork.optional(),
    from: Iso.optional(),
    to: Iso.optional(),
})
    .refine((q) => !(q.from && q.to) || Date.parse(q.from) < Date.parse(q.to), {
    message: 'from должен быть раньше to',
    path: ['from'],
});
/**
 * Пример теневого ответа для сводки (SHADOW-E: только ответы): выбранный
 * шаблон (`text`, у движка `replyText`) на текст комментария. `commentRef` —
 * id комментария в журнале движка (переход в карточку); null — движок не
 * прислал. `wouldWait*` — упёрся бы этот ответ.
 */
export const SocialShadowExampleDTO = z.object({
    commentRef: z.string().nullable().catch(null).default(null),
    account: z.string().nullable().catch(null).default(null),
    kind: z.string().min(1).catch('reply').default('reply'),
    text: z.string().nullable().catch(null).default(null),
    reason: z.string().nullable().catch(null).default(null),
    category: z.string().nullable().catch(null).default(null),
    lang: z.string().nullable().catch(null).default(null),
    /** Текст комментария, на который решал алгоритм. */
    commentText: z.string().nullable().catch(null).default(null),
    authorUsername: z.string().nullable().catch(null).default(null),
    /** Имя автора (Facebook); null — нет / старый движок. */
    authorName: z.string().nullable().catch(null).default(null),
    wouldWaitReason: z.string().nullable().catch(null).default(null),
    wouldWaitText: z.string().nullable().catch(null).default(null),
    decidedAt: Iso.nullable().catch(null).default(null),
});
/**
 * Одна причина «упёрлось бы» в сводке тени (SHADOW-E): сколько действий
 * упёрлось бы всего и по видам, фраза движка.
 */
export const SocialShadowWaitDTO = z.object({
    reason: z.string().min(1),
    total: Count,
    like: Count.catch(0).default(0),
    reply: Count.catch(0).default(0),
    hide: Count.catch(0).default(0),
    delete: Count.catch(0).default(0),
    text: z.string().nullable().catch(null).default(null),
});
/**
 * «Тень за период» (по `decidedAt` теневых решений): сколько сердечек /
 * ответов / скрытий / удалений алгоритм СДЕЛАЛ БЫ (действия, которые ушли бы
 * сразу; упёршиеся сюда не входят — они в `wouldWait`), сколько комментариев
 * отдал бы человеку (`manualQueue`), по скольким ничего (`noAction`), сколько
 * комментариев решено всего (`comments`). `wouldWait` — действий упёрлось бы
 * (лимит / окно ответов / прогрев / стоп), `wouldWaitByReason` — по причинам
 * и видам; `skippedByReason` — пропустил бы (код → число); `byCategory` —
 * решения по категориям комментария; `examples` — примеры ответов. Счётчики
 * видов — строгие (неверная форма — 502, а не «0 лайков»); остальное —
 * терпимое.
 */
const SHADOW_COUNTS_SHAPE = {
    likes: Count,
    replies: Count,
    hides: Count,
    deletes: Count,
    manualQueue: Count,
    noAction: Count,
    comments: Count.nullable().catch(null).default(null),
    wouldWait: Count.nullable().catch(null).default(null),
    wouldWaitByReason: z.array(SocialShadowWaitDTO).catch([]).default([]),
    skippedByReason: z.record(z.string(), Count).catch({}).default({}),
    byCategory: z.record(z.string(), Count).catch({}).default({}),
};
/**
 * Сводка тени по одному аккаунту × сети (`byAccount` движка): те же счётчики
 * и `mode` — режим СЕЙЧАС (после выхода из тени — «Авто»/«Выключено», а
 * решения за период остаются видны). null — движок не прислал.
 */
export const SocialShadowAccountSummaryDTO = z.object({
    account: Name,
    network: SocialNetwork,
    mode: SocialReactionMode.nullable().catch(null).default(null),
    ...SHADOW_COUNTS_SHAPE,
});
export const SocialShadowSummaryResponse = z.object({
    account: z.string().nullable().default(null),
    network: SocialNetwork.nullable().catch(null).default(null),
    from: Iso.nullable().default(null),
    to: Iso.nullable().default(null),
    /**
     * По аккаунтам × сетям, где тень решала за период (независимо от режима
     * сейчас). null — движок не прислал (старый движок).
     */
    byAccount: z.array(SocialShadowAccountSummaryDTO).nullable().catch(null).default(null),
    likes: Count,
    replies: Count,
    hides: Count,
    deletes: Count,
    manualQueue: Count,
    noAction: Count,
    comments: Count.nullable().catch(null).default(null),
    wouldWait: Count.nullable().catch(null).default(null),
    wouldWaitByReason: z.array(SocialShadowWaitDTO).catch([]).default([]),
    skippedByReason: z.record(z.string(), Count).catch({}).default({}),
    byCategory: z.record(z.string(), Count).catch({}).default({}),
    examples: z.array(SocialShadowExampleDTO).catch([]).default([]),
});
// ---------------------------------------------------------------------------
// POST /poll — «Опросить сейчас»
// ---------------------------------------------------------------------------
/** Ручной опрос: весь движок или один аккаунт / одна сеть. */
export const SocialPollRequest = z
    .object({
    account: Name.optional(),
    network: SocialNetwork.optional(),
})
    .strict();
/**
 * `started: true` — опрос запущен в фоне. `reason` — почему не запущен
 * (машинная строка); null — запущен. «Опрос уже идёт» приходит отказом
 * `social_poll_running`, а не этим полем.
 */
export const SocialPollResponse = z.object({
    started: z.boolean(),
    reason: z.string().nullable().default(null),
});
//# sourceMappingURL=social.js.map